import * as React from 'react';
import { useSelector } from 'react-redux';
import { Icon } from 'semantic-ui-react';
import { tl } from 'common/services/i18nService';
import { isViewAsStudent } from 'common/util/shared/sharedUtils';
import { RootState } from '../../../store/rootReducer';
import * as styles from './AiMarkingFullWindow.scss';
import { getAwardedScore } from './aiMarkingAwardedScore';

const HELP_AND_SUPPORT_URL = 'https://support.inspera.com/hc/en-us';

/** Header of the candidate report's Graide tab: tenant logo, help link, and the question being viewed. */
const AiMarkingViewHeader: React.FC = () => {
    const { assessment, context, session } = useSelector((state: RootState) => state);
    const { hasMpLogo, mpLogoUrl, hideQuestionTitles } = assessment;
    const {
        questionNumbering, questionName, score, evaluatorScores, maxScore, rubricType,
    } = context.questionState;
    const isCandidate = Boolean(session.user?.isStudent) || isViewAsStudent();
    const awardedScore = getAwardedScore(evaluatorScores, score);
    // Without rubrics the Graide side panel (and its result) is hidden for candidates, so show marks here.
    const showMarks = isCandidate && !rubricType && awardedScore !== undefined && maxScore !== undefined;
    // Same handling as the candidate report header (StudentReportHeader).
    const logoUrl = mpLogoUrl && mpLogoUrl.indexOf('https://') === 0 ? mpLogoUrl : decodeURIComponent(mpLogoUrl);

    return (
        <header className={styles.header}>
            <div className={styles.topBar}>
                {hasMpLogo ? <img src={logoUrl} alt="logo" className={styles.logo} /> : <span />}
                <a href={HELP_AND_SUPPORT_URL} target="_blank" rel="noopener noreferrer" className={styles.helpLink}>
                    {tl('workspace.plan_help_and_support.title')}
                </a>
            </div>
            <div className={styles.questionBar}>
                <div>
                    {questionNumbering && (
                        <div className={styles.questionNumber}>
                            {`${tl('grading.breadcrumb.question')} ${questionNumbering}`}
                        </div>
                    )}
                    {!(isCandidate && hideQuestionTitles) && <div className={styles.questionName}>{questionName}</div>}
                </div>
                <div className={styles.questionStatus}>
                    {showMarks && (
                        <span className={styles.marks}>
                            {tl('grading.features.table.marksColon')}
                            <b>{awardedScore}</b>
                            {tl('grading.setMark.rubricMarking.table.outOf')}
                            <b>{maxScore}</b>
                        </span>
                    )}
                    {isCandidate && (
                        <span className={styles.marked}>
                            <Icon name="check circle" aria-hidden="true" />
                            {tl('grading.table.filters.markingStatus.marked')}
                        </span>
                    )}
                </div>
            </div>
        </header>
    );
};

export default AiMarkingViewHeader;
